<script setup>
import { useForm, router } from '@inertiajs/vue3';
import { computed, getCurrentInstance } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Card from 'primevue/card';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    globalFixedAdditions: Object,
});

const form = useForm({
    currency: '',
    value: null,
    percentage_addition: 0,
    apply_percentage_to_total: false,
});

const submit = () => {
    form.post(route('exchanges.store'), {
        onStart: () => showLoading('Creating exchange...'),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Exchange created successfully',
                life: 3000
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to create exchange',
                life: 5000
            });
        }
    });
};

const getPreviewFormula = computed(() => {
    const globalFixed = props.globalFixedAdditions?.[form.currency] || 0;
    const fixed = globalFixed / 100;
    const percentage = form.percentage_addition
        ? (form.percentage_addition / 10000) + 1
        : 1;
    const percentFormatted = percentage.toFixed(2);

    if (form.apply_percentage_to_total) {
        return `= (COST + ${fixed}) * ${percentFormatted}`;
    } else {
        return `= (COST * ${percentFormatted}) + ${fixed}`;
    }
});

const percentageDisplay = computed({
    get() {
        return form.percentage_addition / 100;
    },
    set(value) {
        form.percentage_addition = value * 100;
    }
});

// Global fixed addition é gerenciado centralmente, não mais por exchange individual
</script>

<template>
    <AdminMainLayout>
        <div class="mx-auto p-6">
            <div class="grid grid-cols-1 lg:grid-cols-1 gap-6">
                <div class="lg:col-span-2">
                    <Card class="mb-6">
                        <template #title>
                            <div class="flex items-center gap-2">
                                <i class="pi pi-plus text-2xl"></i>
                                <span>{{ $t('Add Exchange Rate') }}</span>
                            </div>
                        </template>

                        <template #content>
                            <form @submit.prevent="submit" class="space-y-6">
                                <!-- Currency & Value -->
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div class="space-y-2">
                                        <label for="currency" class="text-sm font-semibold text-surface-700">
                                            {{ $t('Currency') }} <span class="text-red-500">*</span>
                                        </label>
                                        <InputText
                                            id="currency"
                                            v-model="form.currency"
                                            class="w-full"
                                            :class="{ 'p-invalid': form.errors.currency }"
                                            :placeholder="$t('e.g., USD, BRL, EUR')"
                                            :disabled="form.processing"
                                        />
                                        <small v-if="form.errors.currency" class="text-red-500">{{ form.errors.currency }}</small>
                                    </div>

                                    <div class="space-y-2">
                                        <label for="value" class="text-sm font-semibold text-surface-700">
                                            {{ $t('Value based on the dollar') }} <span class="text-red-500">*</span>
                                        </label>
                                        <InputNumber
                                            id="value"
                                            v-model="form.value"
                                            class="w-full"
                                            :class="{ 'p-invalid': form.errors.value }"
                                            mode="decimal"
                                            :minFractionDigits="2"
                                            :maxFractionDigits="2"
                                            :disabled="form.processing"
                                        />
                                        <small v-if="form.errors.value" class="text-red-500">{{ form.errors.value }}</small>
                                    </div>
                                </div>

                                <!-- Pricing Formula Section -->
                                <div class="border-t pt-6">
                                    <h3 class="text-lg text-surface-900 mb-4 flex items-center gap-2">
                                        <i class="pi pi-calculator text-primary-500"></i>
                                        {{ $t('Suggested Price Formula') }}
                                    </h3>

                                    <!-- Formula Type Selection -->
                                    <div class="mb-6">
                                        <label class="text-sm font-semibold text-surface-700 block mb-3">{{ $t('Formula Type') }}</label>
                                        <div class="flex gap-4">
                                            <div 
                                                class="flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all"
                                                :class="!form.apply_percentage_to_total ? 'border-primary-500 bg-primary-50' : 'border-surface-200 hover:border-surface-300'"
                                                @click="form.apply_percentage_to_total = false"
                                            >
                                                <div class="flex items-center gap-2 mb-2">
                                                    <div class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                                                         :class="!form.apply_percentage_to_total ? 'bg-primary-500 text-white' : 'bg-surface-200 text-surface-600'">
                                                        A
                                                    </div>
                                                    <span class="font-semibold">{{ $t('Standard Formula') }}</span>
                                                </div>
                                                <p class="text-sm text-surface-600 mb-2">{{ $t('Apply percentage to cost, then add fixed value') }}</p>
                                                <code class="text-sm bg-surface-100 px-2 py-1 rounded">(COST × %) + FIXED</code>
                                            </div>

                                            <div 
                                                class="flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all"
                                                :class="form.apply_percentage_to_total ? 'border-primary-500 bg-primary-50' : 'border-surface-200 hover:border-surface-300'"
                                                @click="form.apply_percentage_to_total = true"
                                            >
                                                <div class="flex items-center gap-2 mb-2">
                                                    <div class="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                                                         :class="form.apply_percentage_to_total ? 'bg-primary-500 text-white' : 'bg-surface-200 text-surface-600'">
                                                        B
                                                    </div>
                                                    <span class="font-semibold">{{ $t('Alternative Formula') }}</span>
                                                </div>
                                                <p class="text-sm text-surface-600 mb-2">{{ $t('Add fixed value to cost, then apply percentage') }}</p>
                                                <code class="text-sm bg-surface-100 px-2 py-1 rounded">(COST + FIXED) × %</code>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Formula Parameters -->
                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                        <div class="space-y-2">
                                            <label for="percentage" class="text-sm font-semibold text-surface-700">
                                                {{ $t('Percentage Addition') }}
                                            </label>
                                            <InputNumber
                                                id="percentage"
                                                v-model="percentageDisplay"
                                                mode="decimal"
                                                :maxFractionDigits="1"
                                                suffix="%"
                                                class="w-full"
                                                :disabled="form.processing"
                                            />
                                        </div>

                                        <div class="space-y-2">
                                            <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                                <div class="flex items-center gap-2">
                                                    <i class="pi pi-info-circle text-blue-600"></i>
                                                    <span class="text-sm font-semibold text-blue-800">{{ $t('Fixed Addition') }}</span>
                                                </div>
                                                <p class="text-sm text-blue-700 mt-1">
                                                    {{ $t('Fixed addition values are managed globally for all exchanges') }}
                                                </p>
                                                <p class="text-sm font-mono text-blue-600 mt-1" v-if="globalFixedAdditions && form.currency">
                                                    {{ form.currency }}: {{ (globalFixedAdditions[form.currency] || 0) / 100 }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Formula Preview -->
                                    <div class="bg-surface-50 p-4 rounded-lg">
                                        <label class="text-sm font-semibold text-surface-700 block mb-2">
                                            <i class="pi pi-eye mr-1"></i>
                                            {{ $t('Formula Preview') }}
                                        </label>
                                        <div class="font-mono text-lg bg-white p-3 rounded border">
                                            {{ getPreviewFormula }}
                                        </div>
                                    </div>
                                </div>

                                <!-- Action Buttons -->
                                <div class="flex gap-3 pt-6 border-t">
                                    <Button 
                                        type="submit" 
                                        :label="$t('Save')" 
                                        icon="pi pi-check" 
                                        :loading="form.processing"
                                        size="large"
                                    />
                                    <Button 
                                        :label="$t('Cancel')" 
                                        icon="pi pi-times" 
                                        severity="secondary" 
                                        outlined
                                        size="large"
                                        @click="() => router.visit(route('exchanges.index'))"
                                    />                              
                                </div>

                                <Message v-if="form.recentlySuccessful" severity="success" :closable="false" class="mt-3">
                                    {{ $t('Exchange created successfully.') }}
                                </Message>
                            </form>
                        </template>
                    </Card>
                </div>
            </div>
        </div>
    </AdminMainLayout>
</template>
